import styles from "./UserAccountNotes.module.css";
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Grid, TextField, Button } from "@material-ui/core";

import GlobalMessage from "../../GlobalMessage";
import LoadingMsgBar from "../../LoadingMsgBar";
import { UserAccountNotesTable } from "./UserAccountNotesTable";
import { useStableFunction } from "../../../hooks/useStableFunction";
import NotesService from "../../../Services/NotesService";

import * as Utils from "../../../utils/Utils";

window.analytics ={pageName:'User Account Notes'};

function UserAccountNotes({ history, user }) {
  const location = useLocation();

  // User whose notes we are viewing/editing
  const [selectedUser] = useState(location.state?.selectedUser || null);

  // Form and data states
  const [noteText, setNoteText] = useState("");
  const [noteList, setNoteList] = useState([]);

  // UI states
  const [noteTextError, setNoteTextError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);

  // Load notes for the selected user
  const loadNotes = useStableFunction(async () => {
    try {
      setIsLoadingNotes(true);
      const response = await NotesService.findNotes(selectedUser.id);
      setNoteList(response.data);
    } catch (error) {
      console.error("Error loading user notes:", error);
      setErrorMessage("Error loading user notes. Please try again later.");
    } finally {
      setIsLoadingNotes(false);
    }
  });

  // Load notes when component mounts
  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Validate the note form
  const validateForm = () => {
    if (!noteText.trim()) {
      setNoteTextError("Note is required.");
      return false;
    }
    setNoteTextError("");

    return true;
  };

  // Handle note submission
  const handleSubmit = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (validateForm()) {
      try {
        setIsSaving(true);
        await NotesService.saveNote(selectedUser.id, noteText);

        setSuccessMessage("Note saved successfully.");
        setNoteText("");
        loadNotes();
      } catch (error) {
        console.error("Error saving note:", error);
        setErrorMessage(
          `There was an error saving your note. Please try again or contact support at 
          <a href="mailto:coventryprovidertechsupport@cvty.us.com">coventryprovidertechsupport@cvty.us.com</a>.`
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Handle successful note edit from the table
  const handleNoteSaved = () => {
    setSuccessMessage("Note saved successfully.");
  };

  const getTitle = () => {
    if (!Utils.isEmpty(selectedUser.userNm)) {
      return `User Account Notes for ${selectedUser.userNm}`;
    } else {
      return `User Account Notes for ${selectedUser.userFirstName} ${selectedUser.userLastName}`;
    }
  }

  return (
    <div className={styles.requestAccount}>
      <h1 className="pageTitle">
        {getTitle()}
      </h1>

      {isSaving && <LoadingMsgBar loadMsg="Saving note..." />}
      {errorMessage && <GlobalMessage message={errorMessage} type="error" />}
      {successMessage && (
        <GlobalMessage type="success" message={successMessage} />
      )}

      <div className="App-note-Field App-error-font App-error-color">
        Note: Fields marked with an asterisk(*) are required.
      </div>

      {/* Note Entry Form */}
      <Grid container spacing={3}>
        <Grid item xs={8}>
  <TextField
    id="note"
    label="Note"
    error={!!noteTextError}
    helperText={noteTextError}
    value={noteText}
    onChange={(e) => setNoteText(e.target.value)}
    multiline
    minRows={4}
    inputProps={{ maxLength: 1000 }}
    fullWidth
    required
    autoFocus
    placeholder="Enter your note here..."
    disabled={isSaving}
  />
  <div className={styles.characterCounter}>
    {noteText.length} / 1000 characters
  </div>
</Grid>

        <Grid item xs={8}>
          <div className="buttonBar" style={{ paddingTop: 0 }}>
            <Button onClick={handleSubmit} type="button" disabled={isSaving}>
              Save
            </Button>
            <Button
              onClick={() => history.goBack()}
              type="button"
              variant="outlined"
            >
              Cancel
            </Button>
          </div>
        </Grid>
      </Grid>

      {/* Notes Table */}
      <Grid container spacing={3}>
        <Grid item xs={8}>
          {isLoadingNotes ? (
            <LoadingMsgBar loadMsg="Loading notes..." />
          ) : (
            <UserAccountNotesTable
              noteList={noteList}
              currentUser={user}
              selectedUser={selectedUser}
              onRefreshTable={loadNotes}
              onSaveNote={handleNoteSaved}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export { UserAccountNotes };
